/**
 * The support desk: what players typed at the bot that no command answered,
 * and the operator's replies back through it.
 *
 * Until this screen could reply, a player asking "where is my deposit?" was
 * visible here and answerable only somewhere else — an operator read the
 * question in the console, then went hunting for the person in Telegram, in a
 * chat nothing here could see. The conversation now lives in one place, and the
 * player experiences it as one conversation with one bot.
 *
 * Two panes rather than a table: a support queue is read by conversation, not
 * by message. The left is who is waiting, most recently active first; the right
 * is one exchange in the order it was said, with the reply box under it.
 *
 * Kept 30 days, like the inbound record always was. The audit trail keeps its
 * own permanent row for every reply an admin sent, so what outlives the
 * conversation is what an operator might later have to answer for.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { at } from '../format.ts';
import { ErrorBanner, Spinner } from '../components/ui.tsx';
import { BackIcon } from '../components/icons.tsx';
import type { AdminView, SupportThreadView, SupportMessageView } from '../api/types.ts';

export function Inbox({ me }: { me: AdminView }) {
  const [threads, setThreads] = useState<SupportThreadView[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  /**
   * Phone only: which of the two panes is on screen.
   *
   * On a monitor both are, and this is ignored. On a phone they cannot both
   * be — stacked, the reply box for the third person in the queue sits below
   * a scrollable list of everyone above them, and the reply box is the point
   * of the screen. So the list opens first and picking someone replaces it,
   * with `Back` to return. `selected` cannot stand in for this: it is
   * pre-filled on arrival so the desk layout never shows an empty right pane.
   */
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const loadThreads = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const res = await api.supportThreads();
      setThreads(res.threads);
      // Open the first conversation on arrival: an operator coming here almost
      // always wants the most recent one, and an empty right-hand pane beside a
      // populated list reads as a broken screen.
      setSelected((current) => current ?? res.threads[0]?.telegramId ?? null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
    // Opening the screen is what "read" means for the nav badge. Failures are
    // ignored: a badge that fails to clear is a much smaller problem than an
    // error banner over an inbox that loaded perfectly well.
    void api.markInboxSeen().catch(() => undefined);
  }, [loadThreads]);

  const active = threads?.find((t) => t.telegramId === selected) ?? null;

  return (
    <section>
      <header className="pagehead">
        <h1>Inbox</h1>
      </header>

      <ErrorBanner error={error} />
      {threads === null ? <Spinner /> : null}

      {threads && threads.length === 0 ? (
        <div className="card">
          <p className="muted">
            Nothing yet. Anything a player types at the bot that is not one of its commands
            lands here, and you can answer from this screen.
          </p>
        </div>
      ) : null}

      {threads && threads.length > 0 ? (
        <div className="inbox" data-view={reading ? 'thread' : 'list'}>
          <div className="inbox__pane inbox__pane--list card card--compact inbox__list">
            {threads.map((t) => (
              <button
                key={t.telegramId}
                type="button"
                className={`inbox__thread${t.telegramId === selected ? ' inbox__thread--on' : ''}`}
                onClick={() => {
                  setSelected(t.telegramId);
                  setReading(true);
                }}
              >
                <span className="inbox__who">
                  {t.username ? `@${t.username}` : t.fullName ?? t.telegramId}
                  {t.awaitingReply > 0 ? (
                    <span className="inbox__waiting">{t.awaitingReply}</span>
                  ) : null}
                </span>
                <span className="inbox__preview">
                  {t.lastDirection === 'out' ? <span className="muted">You: </span> : null}
                  {t.lastMessage}
                </span>
                <span className="muted inbox__when">{at(t.lastAt)}</span>
              </button>
            ))}
          </div>

          {active ? (
            <div className="inbox__pane inbox__pane--thread">
              <button
                type="button"
                className="button button--ghost button--icon inbox__back"
                onClick={() => setReading(false)}
              >
                <BackIcon />
                All conversations
              </button>
              <Conversation
                key={active.telegramId}
                thread={active}
                canReply={can(me.role, 'user.manage')}
                onSent={() => void loadThreads()}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function Conversation({
  thread,
  canReply,
  onSent,
}: {
  thread: SupportThreadView;
  canReply: boolean;
  onSent: () => void;
}) {
  const [messages, setMessages] = useState<SupportMessageView[] | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await api.supportThread(thread.telegramId);
      setMessages(res.messages);
    } catch (err) {
      setError(err);
    }
  }, [thread.telegramId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function send(): Promise<void> {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.replyToSupportThread(thread.telegramId, text);
      // Cleared only after the send resolved: a draft wiped optimistically is a
      // reply an operator has to retype from memory when the send fails.
      setDraft('');
      await load();
      onSent();
    } catch (err) {
      setError(err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="card inbox__conversation">
      <h2>
        {thread.username ? `@${thread.username}` : thread.fullName ?? 'Player'}{' '}
        <span className="muted">· {thread.telegramId}</span>
      </h2>

      <ErrorBanner error={error} />
      {messages === null ? <Spinner /> : null}

      <div className="inbox__messages">
        {messages?.map((m) => (
          <div key={m.id} className={`inbox__bubble inbox__bubble--${m.direction}`}>
            <div className="inbox__text">{m.message}</div>
            <div className="muted inbox__meta">
              {m.direction === 'out' ? `${m.adminUsername ?? 'support'} · ` : ''}
              {at(m.receivedAt)}
            </div>
          </div>
        ))}
      </div>

      {!thread.userId ? (
        <p className="muted">
          This person has no account yet, so the bot has no chat to deliver into. They need to
          press Start on the bot before they can be answered here.
        </p>
      ) : !canReply ? (
        <p className="muted">
          Your role can read the inbox but not reply — replying speaks to a player as the
          business, over the operator&rsquo;s own bot.
        </p>
      ) : (
        <div className="inbox__reply">
          <textarea
            className="input"
            rows={3}
            placeholder="Reply as the bot…"
            value={draft}
            disabled={sending}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="button"
            className="button button--primary"
            disabled={sending || draft.trim() === ''}
            onClick={() => void send()}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}
